import { isLicensed } from '@/utils/licenseStatus';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { moveLicense, type ApiVendoDevice } from '@/api/vendo';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import type { VendoDevice } from '@/pages/Admin/Vendo';

interface Props {
   data: {
      device: VendoDevice;
   };
}

/**
 * Move an ACTIVE device's paid license onto another of the owner's devices
 * (no wallet movement; conserves the remaining time). Server-enforced,
 * including the 24-hour move cooldown; this UI just drives it.
 */

/** 24 hours — keep in sync with the server's MOVE_COOLDOWN_MS. */
export const MOVE_COOLDOWN_HOURS = 24;

/**
 * How long until this license can be moved again, in words — "23 hours",
 * "1 hour", "40 minutes" — or null when it can be moved now. Rounded UP, so
 * the button never says a move is available a minute before the server
 * agrees.
 */
export const moveCooldownLeft = (
   licenseMovedAt: string | Date | null | undefined,
   now: number = Date.now()
): string | null => {
   if (!licenseMovedAt) return null;
   const until = new Date(licenseMovedAt).getTime() + MOVE_COOLDOWN_HOURS * 3600_000;
   const ms = until - now;
   if (!Number.isFinite(ms) || ms <= 0) return null;
   if (ms < 3600_000) {
      const min = Math.max(1, Math.ceil(ms / 60_000));
      return `${min} minute${min === 1 ? '' : 's'}`;
   }
   const hr = Math.ceil(ms / 3600_000);
   return `${hr} hour${hr === 1 ? '' : 's'}`;
};

/** Checked in at least once — router agent, REST probe or NodeMCU. Same rule
 *  as the server's isReadyMoveTarget. */
export const everCheckedIn = (d: ApiVendoDevice): boolean =>
   !!(d.router?.lastSeenAt || d.router?.lastRestOkAt || d.lastHeartbeat || d.nodeMcu?.lastHeartbeat);

/**
 * One picker line per vendo. Several records can share a name (an operator who
 * re-registers the same router), so the line carries the id and when it was
 * registered — "HADIYA · ESP-25F92E75 · registered Oct 29, 2026".
 */
export const moveTargetLabel = (t: Pick<ApiVendoDevice, 'name' | 'deviceId' | 'createdAt'>): string =>
   [t.name, t.deviceId, t.createdAt ? `registered ${formatRelativeDate(t.createdAt)}` : null]
      .filter(Boolean)
      .join(' · ');

const ManageLicense = ({ data }: Props) => {
   const { device } = data;
   const { handleClose } = useUIStore();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const allDevices = useVendoDevicesStore((s) => s.devices);

   const [busy, setBusy] = useState<'move' | null>(null);
   const [targetId, setTargetId] = useState('');

   // Move targets = the owner's other devices that can actually receive a
   // license. Excludes: this device; sold devices (not the current owner);
   // devices still in setup / "awaiting" (setupStatus !== 'done'); devices
   // that have never checked in (HADIYA, 2026-10-30: the lifetime went onto
   // records no router talked to — the server refuses these too); and devices
   // already licensed (active or lifetime — no point moving onto them).
   const targets = useMemo(
      () =>
         allDevices.filter(
            (d) =>
               d._id !== device._id &&
               d.isCurrentOwner !== false &&
               d.setupStatus === 'done' &&
               everCheckedIn(d) &&
               !isLicensed(d.licenseStatus)
         ),
      [allDevices, device._id]
   );

   const cooldownLeft = moveCooldownLeft(device.licenseMovedAt);
   const inCooldown = cooldownLeft !== null;

   const handleMove = async () => {
      if (busy || !targetId || inCooldown) return;
      setBusy('move');
      try {
         await moveLicense(device._id, targetId);
         refetchDevices();
         const target = targets.find((t) => t._id === targetId);
         toast.success(
            `Moved ${device.name}'s license to ${target?.name ?? 'the device'}`,
            { autoClose: 2500 }
         );
         handleClose();
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Could not move the license');
      } finally {
         setBusy(null);
      }
   };

   return (
      <Wrapper>
         <Summary>
            <DeviceTitle>{device.name}</DeviceTitle>
            <ActivePill>
               {device.licenseStatus === 'lifetime' ? '✓ Lifetime license' : '✓ Active license'}
            </ActivePill>
         </Summary>

         {/* Move */}
         <Card>
            <CardHead>
               <CardIcon>↔️</CardIcon>
               <CardTitle>Move to another device</CardTitle>
            </CardHead>
            <CardDesc>
               {device.licenseStatus === 'lifetime'
                  ? 'Move the lifetime license onto one of your other devices. No credit is used — this device\'s license ends.'
                  : 'Transfer the remaining time onto one of your other devices. No credit is used — the time just moves; this device\'s license ends.'}
               You can move a license again after <strong>24 hours</strong>.
            </CardDesc>
            {inCooldown ? (
               <MoveRow>
                  <CooldownBtn type="button" disabled>
                     🔒 Move available in {cooldownLeft}
                  </CooldownBtn>
               </MoveRow>
            ) : (
               <>
                  <Select
                     value={targetId}
                     onChange={(e) => setTargetId(e.target.value)}
                  >
                     <option value="">
                        {targets.length ? 'Choose a device…' : 'No vendo can take it yet — finish setting one up first'}
                     </option>
                     {targets.map((t) => (
                        <option key={t._id} value={t._id}>
                           {moveTargetLabel(t)}
                        </option>
                     ))}
                  </Select>
                  <MoveRow>
                     <MoveBtn
                        type="button"
                        disabled={!targetId || busy === 'move'}
                        onClick={handleMove}
                     >
                        {busy === 'move' ? 'Moving…' : 'Move license'}
                     </MoveBtn>
                  </MoveRow>
               </>
            )}
         </Card>

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Close" type="outline" size="small" />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

export default ManageLicense;

const Wrapper = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
   padding: 4px 2px;
`;

const Summary = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;

const DeviceTitle = styled.h3`
   margin: 0;
   font-size: 16px;
   color: ${colors.DARK};
`;

const ActivePill = styled.span`
   font-size: 12px;
   font-weight: 600;
   color: #0a7d33;
   background: #e7f7ec;
   border-radius: 999px;
   padding: 4px 10px;
`;

const Card = styled.div`
   border: 1px solid #e6e6e6;
   border-radius: 12px;
   padding: 14px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const CardHead = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const CardIcon = styled.span`
   font-size: 18px;
`;

const CardTitle = styled.span`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.DARK};
`;

const CardDesc = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.45;
   color: ${colors.GRAY};
`;

const Select = styled.select`
   width: 100%;
   padding: 9px 10px;
   border: 1px solid #e6e6e6;
   border-radius: 8px;
   font-size: 14px;
   background: #fff;
`;

// Action row — right-aligned, matching the modal's Close button.
const MoveRow = styled.div`
   display: flex;
   justify-content: flex-end;
`;

// Primary (blue) move button. Greys out + blocks the click when disabled
// (no target selected or mid-move), so only the button itself is clickable.
const MoveBtn = styled.button`
   padding: 8px 16px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   &:hover:not(:disabled) {
      opacity: 0.9;
   }
   &:disabled {
      background: #e6e6e6;
      color: #9aa0a6;
      cursor: not-allowed;
   }
`;

// Disabled, orange — shown while a moved license is within its cooldown.
const CooldownBtn = styled.button`
   padding: 8px 14px;
   border: 1px solid #f0a020;
   border-radius: 8px;
   background: #fff7e6;
   color: #b45309;
   font-size: 13px;
   font-weight: 600;
   cursor: not-allowed;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
`;
